// src/components/PWAInstaller.jsx
import React, { useState, useEffect } from 'react';
// swapped lucide-react for react-icons/fa
import { FaDownload, FaTimes, FaMobileAlt, FaDesktop } from 'react-icons/fa';

const PWAInstaller = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const checkStandalone = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    const checkIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

    setIsStandalone(checkStandalone());
    setIsIOS(checkIOS());

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const dismissedTime = localStorage.getItem('pwa-banner-dismissed');
      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (!dismissedTime || Date.now() - parseInt(dismissedTime) > oneDayInMs) {
        setShowInstallBanner(true);
      }
    };

    const handleAppInstalled = () => {
      setShowInstallBanner(false);
      setDeferredPrompt(null);
      showInstallSuccessToast();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIOS) setShowIOSInstructions(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    console.log(outcome === 'accepted' ? 'User accepted the install prompt' : 'User dismissed the install prompt');

    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleBannerDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  const showInstallSuccessToast = () => {
    if (window.showToast) {
      window.showToast('App installed successfully!', 'success');
    }
  };

  const IOSInstructions = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-sm w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Install ADTAC App</h3>
          <button onClick={() => setShowIOSInstructions(false)} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>

        <div className="space-y-4 text-sm text-gray-600">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-semibold text-blue-600">1</div>
            <div>Tap the share button in Safari (square with arrow pointing up)</div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-semibold text-blue-600">2</div>
            <div>Scroll down and tap "Add to Home Screen"</div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-semibold text-blue-600">3</div>
            <div>Tap "Add" to install the ADTAC app</div>
          </div>
        </div>

        <button
          onClick={() => setShowIOSInstructions(false)}
          className="mt-6 w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
        >
          Got it!
        </button>
      </div>
    </div>
  );

  if (isStandalone) return null;

  return (
    <>
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-red-600 to-red-700 text-white p-4 shadow-lg z-40">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaMobileAlt className="h-6 w-6" />
              <div>
                <p className="font-semibold text-sm">Install ADTAC App</p>
                <p className="text-xs opacity-90">Get quick access to training and consultancy services</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handleInstallClick}
                className="bg-white text-red-600 px-4 py-2 rounded-md text-sm font-semibold hover:bg-gray-100 transition-colors flex items-center space-x-2"
              >
                <FaDownload size={16} />
                <span>Install</span>
              </button>
              <button onClick={handleBannerDismiss} className="text-white hover:text-gray-200 p-1">
                <FaTimes size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {deferredPrompt && !showInstallBanner && (
        <button
          onClick={handleInstallClick}
          className="fixed bottom-6 right-6 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-30 flex items-center space-x-2"
          title="Install ADTAC App"
        >
          <FaDownload size={20} />
          <span className="hidden sm:inline text-sm font-medium">Install App</span>
        </button>
      )}

      {showIOSInstructions && <IOSInstructions />}
    </>
  );
};

export default PWAInstaller;
